import { PublicLayout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardFooter } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { useState, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { Eye, EyeOff, Building2, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { SEOHead } from "@/components/seo-head";

interface GymFormData {
  gymName: string;
  slug: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  timezone: string;
  adminEmail: string;
  adminPassword: string;
  adminFirstName: string;
  adminLastName: string;
  adminPhone: string;
}

const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
];

export default function RegisterGym() {
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [slugTimer, setSlugTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<GymFormData>({
    defaultValues: {
      gymName: "",
      slug: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      timezone: "America/Chicago",
      adminEmail: "",
      adminPassword: "",
      adminFirstName: "",
      adminLastName: "",
      adminPhone: "",
    },
  });

  const slugValue = watch("slug");

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const checkSlugAvailability = useCallback((slug: string) => {
    if (!slug || slug.length < 2) {
      setSlugStatus("idle");
      return;
    }
    setSlugStatus("checking");
    apiFetch(`/onboarding/gym-check/${encodeURIComponent(slug)}`)
      .then((data: { available: boolean }) => {
        setSlugStatus(data.available ? "available" : "taken");
      })
      .catch(() => {
        setSlugStatus("idle");
      });
  }, []);

  const handleGymNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    const slug = generateSlug(name);
    setValue("slug", slug);
    if (slugTimer) clearTimeout(slugTimer);
    const timer = setTimeout(() => checkSlugAvailability(slug), 500);
    setSlugTimer(timer);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slug = generateSlug(e.target.value);
    setValue("slug", slug);
    if (slugTimer) clearTimeout(slugTimer);
    const timer = setTimeout(() => checkSlugAvailability(slug), 500);
    setSlugTimer(timer);
  };

  const onSubmit = async (data: GymFormData) => {
    if (slugStatus === "taken") {
      toast({ title: "Slug unavailable", description: "Please choose a different gym URL slug.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await apiFetch<{ token: string; admin: { firstName: string } }>("/onboarding/gym", {
        method: "POST",
        body: JSON.stringify(data),
      });
      localStorage.setItem("token", res.token);
      await refreshUser();
      toast({ title: "Gym created!", description: `Welcome to OPEN MAT, ${res.admin.firstName}!` });
      setLocation("/admin/dashboard");
    } catch (err: any) {
      toast({
        title: "Registration failed",
        description: err.message || "Could not create gym account",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <SEOHead
        title="Register Your Gym — Open Mat"
        description="Sign up your martial arts gym on Open Mat. Manage private lessons, trainers, scheduling, and payments — all in one platform. Free to get started."
        canonicalPath="/register-gym"
        ogType="website"
      />
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-2xl glass-panel border-white/5 bg-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-3xl font-display font-bold text-white uppercase tracking-wide leading-none">Sign Up Your Gym</h1>
            <CardDescription className="text-muted-foreground mt-2">Register your gym on OPEN MAT and start managing lessons</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Gym Information</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="gymName">Gym Name *</Label>
                    <Input
                      id="gymName"
                      placeholder="Elite MMA Academy"
                      className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                      {...register("gymName", { required: "Gym name is required", onChange: handleGymNameChange })}
                    />
                    {errors.gymName && <p className="text-sm text-red-400">{errors.gymName.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slug">URL Slug *</Label>
                    <div className="relative">
                      <Input
                        id="slug"
                        placeholder="elite-mma-academy"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl pr-10"
                        value={slugValue}
                        onChange={handleSlugChange}
                      />
                      <input type="hidden" {...register("slug", { required: "Slug is required" })} />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {slugStatus === "checking" && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
                        {slugStatus === "available" && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                        {slugStatus === "taken" && <XCircle className="w-4 h-4 text-red-400" />}
                      </div>
                    </div>
                    {slugStatus === "taken" && <p className="text-sm text-red-400">This slug is already taken</p>}
                    {slugValue && slugStatus !== "taken" && (
                      <p className="text-xs text-muted-foreground">Your gym will be at: openmat.com/gym/{slugValue}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      placeholder="123 Main Street"
                      className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                      {...register("address")}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="Austin"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                        {...register("city")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        placeholder="TX"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                        {...register("state")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zipCode">Zip Code</Label>
                      <Input
                        id="zipCode"
                        placeholder="78701"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                        {...register("zipCode")}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <select
                      id="timezone"
                      className="flex h-12 w-full rounded-xl border bg-background border-white/10 focus:border-primary px-3 py-2 text-sm text-white appearance-none cursor-pointer"
                      {...register("timezone")}
                    >
                      {US_TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/5 pt-6">
                <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Admin Account</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="adminFirstName">First Name *</Label>
                      <Input
                        id="adminFirstName"
                        placeholder="John"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                        {...register("adminFirstName", { required: "First name is required" })}
                      />
                      {errors.adminFirstName && <p className="text-sm text-red-400">{errors.adminFirstName.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="adminLastName">Last Name *</Label>
                      <Input
                        id="adminLastName"
                        placeholder="Doe"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                        {...register("adminLastName", { required: "Last name is required" })}
                      />
                      {errors.adminLastName && <p className="text-sm text-red-400">{errors.adminLastName.message}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adminEmail">Email *</Label>
                    <Input
                      id="adminEmail"
                      type="email"
                      placeholder="you@yourgym.com"
                      className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                      {...register("adminEmail", { required: "Email is required" })}
                    />
                    {errors.adminEmail && <p className="text-sm text-red-400">{errors.adminEmail.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adminPhone">Phone (optional)</Label>
                    <Input
                      id="adminPhone"
                      placeholder="+1 (555) 000-0000"
                      className="bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                      {...register("adminPhone")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adminPassword">Password *</Label>
                    <div className="relative">
                      <Input
                        id="adminPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Min 6 characters"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl pr-12"
                        {...register("adminPassword", { required: "Password is required", minLength: { value: 6, message: "Password must be at least 6 characters" } })}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.adminPassword && <p className="text-sm text-red-400">{errors.adminPassword.message}</p>}
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || slugStatus === "taken"}
                className="w-full h-14 text-lg font-bold bg-gradient-to-r from-primary to-[#f0d060] text-black rounded-xl mt-2"
              >
                {isSubmitting ? "Creating Your Gym..." : "Create Gym & Admin Account"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="justify-center text-sm text-muted-foreground border-t border-white/5 pt-6">
            Already have an account? <Link href="/login"><span className="text-primary hover:underline ml-1 cursor-pointer">Sign in</span></Link>
          </CardFooter>
        </Card>
      </div>
    </PublicLayout>
  );
}
